import { stripSourcemapMarkerOutputs } from "../sourcemap-markers";
import {
  normalizeLiquidTagEntities,
  normalizeNestedOutputTags,
} from "../template-vars";
import {
  ELEMENT_KEY_CHARS,
  isElementKey,
  keyCore,
  parseElementKey,
  withSurface,
} from "./element-key";
import {
  ALT_SLOT,
  BODY_CORE,
  HREF_SLOT,
  isTranslationSlot,
  SRC_SLOT,
  type LocalizableElement,
  localizableElement,
  PREHEADER_CORE,
  SUBJECT_CORE,
  SUBTITLE_CORE,
  TITLE_CORE,
  translationRowId,
  type TranslationSlot,
} from "./elements";
import {
  decodeMarkupEntities,
  normalizeLineBreaks,
  normalizeSourceHtml,
} from "./normalize";

/**
 * The vocabulary of a stamped body. One Liquid block tag wraps every translatable
 * range, and the render pass that already runs resolves it:
 *
 *   {% ht_loc 0a1b2c3d-… text %}Shop now{% endht_loc %}
 *   {% ht_loc k3d08e text off %}Terms apply.{% endht_loc %}
 *   {% ht_loc subject text %}Your order shipped{% endht_loc %}
 *   <a href="{% ht_loc 0a1b2c3d-… href %}https://…{% endht_loc %}">
 *   <img src="{% ht_loc b22c0f11-… src %}https://cdn.example.com/hero.png{% endht_loc %}"
 *        alt="{% ht_loc b22c0f11-… alt %}Model wearing the jacket{% endht_loc %}">
 *
 * The key is written **unquoted**. An attribute-slot tag renders inside `href="…"`,
 * where the key's opening quote would close the attribute and every HTML parser would
 * then read the value as `{% ht_loc `.
 *
 * The slot has **no default**. A defaulted slot would let an argument-less tag inside
 * an attribute emit raw text-mode output, which is an injection. A tag naming no
 * recognized slot is malformed: still a tag, so the stamper leaves it alone rather than
 * wrapping it twice, but carrying `slot: null` so nothing translates it.
 *
 * The tag wraps its default content rather than replacing it, so a miss renders the
 * source language instead of a blank, in every slot.
 *
 * Every tag is written by a stamper at save time. Nothing in the product authors one by
 * hand, so the tags a body carries are the ones a walk put there.
 */

export const LOC_TAG = "ht_loc";

/**
 * Wraps the root `lang` value as `<html lang="{% ht_lang %}en{% endht_lang %}">`, with the authored value as the body.
 * It is not a translatable element, so `hasLocalizationTags` and the element walks skip it.
 */
export const LANG_TAG = "ht_lang";

// Matches an argumented opener too, since the parser ignores arguments rather than refusing them.
const LANG_OPEN_TAG_SOURCE = `\\{%-?\\s*${LANG_TAG}\\b[^%]*?-?%\\}`;

const LANG_CLOSE_TAG_SOURCE = `\\{%-?\\s*end${LANG_TAG}\\s*-?%\\}`;

export function buildLangTag(authoredLang: string): string {
  return `{% ${LANG_TAG} %}${authoredLang}{% end${LANG_TAG} %}`;
}

export function hasLangTag(html: string): boolean {
  return new RegExp(LANG_OPEN_TAG_SOURCE).test(html);
}

export const OFF_FLAG = "off";

/**
 * The bare words are captured as one blob rather than as ordered optional groups: a
 * regex ordering two bare words either rejects `off text` or accepts `off off`. Keeping
 * the slot inside the blob is also what lets a slot-less tag still match, so the walk
 * sees a malformed tag instead of bytes the stamper would wrap a second time.
 */
export const TAG_ARGS = `([${ELEMENT_KEY_CHARS}]+)((?:\\s+[a-z_]+)*)`;

const OPEN_TAG_SOURCE = `\\{%-?\\s*${LOC_TAG}\\s+${TAG_ARGS}\\s*-?%\\}`;

const CLOSE_TAG_SOURCE = `\\{%-?\\s*end${LOC_TAG}\\s*-?%\\}`;

/**
 * Tags never nest: the stamper wraps a line only when it contains no other tag, and
 * nothing in the product authors one by hand. That is what lets a whole span be one
 * non-greedy match — the first closer after an opener is always its own.
 */
const TAG_SPAN_SOURCE = `(${OPEN_TAG_SOURCE})([\\s\\S]*?)${CLOSE_TAG_SOURCE}`;

/** What a tag's arguments resolved to. */
export type TagArguments = {
  elementKey: string;
  /** Null when the tag names no recognized slot — malformed, but still a tag. */
  slot: TranslationSlot | null;
  off: boolean;
};

/**
 * Unknown words are ignored and a missing slot yields null rather than throwing. A tag
 * is authored bytes in a customer's HTML, so a typo must cost one element its
 * translation rather than failing the whole body at parse time — which at send time
 * costs a fragment of recipients their email.
 */
export function readTagArguments(
  elementKey: string,
  words: string,
): TagArguments {
  const parsed = words.trim().split(/\s+/).filter(Boolean);
  return {
    elementKey,
    slot: parsed.find(isTranslationSlot) ?? null,
    off: parsed.includes(OFF_FLAG),
  };
}

/** One tag occurrence, as offsets into the string it was found in. */
export type TagSpan = TagArguments & {
  /** Offset of the opening `{%`. */
  start: number;
  /** Offset just past the closing `%}`. */
  end: number;
  innerStart: number;
  /** Raw inner source. NOT canonicalized — pass through `tagSourceText`. */
  inner: string;
};

/**
 * Every localization tag, in document order.
 *
 * Unbalanced delimiters are dropped rather than repaired: an unmatched closer has no
 * opener to belong to, and an unmatched opener leaves nothing to bound its content. Both
 * mean an edit damaged a tag the stamper wrote, and both fail loudly at the Save-time
 * Liquid parse.
 */
export function matchTagSpans(html: string): TagSpan[] {
  if (!hasLocalizationTags(html)) return [];
  const spans: TagSpan[] = [];
  for (const match of html.matchAll(new RegExp(TAG_SPAN_SOURCE, "g"))) {
    const start = match.index ?? 0;
    const innerStart = start + (match[1]?.length ?? 0);
    spans.push({
      start,
      end: start + match[0].length,
      innerStart,
      inner: match[4] ?? "",
      ...readTagArguments(match[2] ?? "", match[3] ?? ""),
    });
  }
  return spans;
}

/** Over-approximate pre-check; the patterns decide what is actually a tag. */
export function hasLocalizationTags(html: string): boolean {
  return html.includes(LOC_TAG);
}

/** Whether a slice of markup contains an opening tag. */
export function containsTag(html: string): boolean {
  return new RegExp(OPEN_TAG_SOURCE).test(html);
}

/** The arguments of the first opening tag, or null if there isn't one. */
export function readFirstTag(html: string): TagArguments | null {
  const match = new RegExp(OPEN_TAG_SOURCE).exec(html);
  if (!match) return null;
  return readTagArguments(match[1] ?? "", match[2] ?? "");
}

/**
 * The tag wrapping this string *in its entirety*, which is a different question from
 * whether it contains one: a line whose link is tagged but whose own words are not
 * still needs wrapping, and `containsTag` would answer yes.
 *
 * Surrounding whitespace is ignored, so a value nudged with a stray space is still
 * recognized as stamped rather than wrapped twice.
 */
export function readWrappingTag(html: string): TagSpan | null {
  const trimmed = html.trim();
  const offset = html.indexOf(trimmed);
  const wrapping = matchTagSpans(html).find(
    (span) => span.start === offset && span.end === offset + trimmed.length,
  );
  return wrapping ? { ...wrapping } : null;
}

/**
 * Rejected rather than escaped, because a key is written unquoted: one containing ` %}`
 * closes the tag and injects Liquid into content the dispatcher later renders with
 * profile data in scope. Not every key reaches here already charset-checked — the
 * Beefree lane derives one from a module uuid in the design JSON, which arrives over
 * the wire. Anything `TAG_ARGS` could not read back is unusable regardless, so failing
 * the save is strictly better than storing it.
 */
export function buildOpenTag(args: {
  elementKey: string;
  slot: TranslationSlot;
  off?: boolean;
}): string {
  if (!isElementKey(args.elementKey)) {
    throw new Error(
      `Refusing to build a ${LOC_TAG} tag for element key "${args.elementKey}": a key may contain only ${ELEMENT_KEY_CHARS}.`,
    );
  }
  const offArg = args.off ? ` ${OFF_FLAG}` : "";
  return `{% ${LOC_TAG} ${args.elementKey} ${args.slot}${offArg} %}`;
}

export function buildTag(args: {
  elementKey: string;
  slot: TranslationSlot;
  inner: string;
  off?: boolean;
}): string {
  return `${buildOpenTag(args)}${args.inner}{% end${LOC_TAG} %}`;
}

/**
 * Selection is a property of the element carried in its own tag, so unchecking one is
 * a content edit that Save persists like any other.
 *
 * Every slot of one element at once, by handing {@link setOffFlags} an element-keyed
 * decision — that setter is row-keyed, and the write path uses it directly with the whole
 * selection. This is the shorthand for one element.
 */
export function setElementOff(
  html: string,
  elementKey: string,
  off: boolean,
): string {
  return setOffFlags(html, (row) =>
    row.elementKey === elementKey ? off : row.off,
  );
}

/** Whether a tag should carry `off`, given the tag as it stands. */
export type OffFlagDecider = (row: {
  elementKey: string;
  slot: TranslationSlot;
  off: boolean;
}) => boolean;

/**
 * The whole selection in one pass, for a caller that holds the desired state of every
 * row rather than one toggle: `decide` is asked about each tag and returns what its
 * flag should be, so returning `row.off` leaves that tag alone.
 *
 * Row-level rather than element-level, because a tag carries its own slot — a line's
 * words can stay in translation while its link drops out.
 */
export function setOffFlags(html: string, decide: OffFlagDecider): string {
  if (!hasLocalizationTags(html)) return html;
  return html.replace(
    new RegExp(OPEN_TAG_SOURCE, "g"),
    (match, key: string, words: string) => {
      const args = readTagArguments(key, words);
      // Rewriting a malformed tag would mean inventing the slot it failed to name.
      if (args.slot === null) return match;
      const off = decide({ elementKey: key, slot: args.slot, off: args.off });
      if (args.off === off) return match;
      return buildOpenTag({ elementKey: key, slot: args.slot, off });
    },
  );
}

/**
 * The canonical source text a tag stands for.
 *
 * The element reader and the Liquid tag's parse hook both call this and must agree, or
 * a translation is written against one string and looked up by another.
 *
 * Sourcemap markers are stripped first: preview instrumentation inserts literals
 * *inside* a tag body, and hashing those would make the sourcemap render miss every
 * translation the plain render found.
 *
 * A malformed tag has no canonical text — there is no way to know whether its body is
 * markup to normalize or a literal attribute value — so it reports empty, which every
 * caller already treats as not translatable.
 */
export function tagSourceText(
  slot: TranslationSlot | null,
  rawInner: string,
  /** Names the SMS body, whose line breaks are copy rather than markup whitespace. */
  elementKey?: string,
): string {
  if (slot === null) return "";
  // The render parses a repaired template, so a hash taken over the unrepaired bytes
  // addresses a row the render will never look up.
  const inner = normalizeNestedOutputTags(
    normalizeLiquidTagEntities(
      stripLocalizationTags(stripSourcemapMarkerOutputs(rawInner)),
    ),
  );
  if (slot === HREF_SLOT || slot === SRC_SLOT) {
    // Decoded the same way the walk decoded it out of the attribute.
    return decodeMarkupEntities(inner.trim());
  }
  if (slot === ALT_SLOT) return inner.trim();
  if (elementKey !== undefined && keyCore(elementKey) === BODY_CORE) {
    return normalizeLineBreaks(inner).trim();
  }
  return normalizeSourceHtml(inner).text;
}

/** Every tag replaced by its default content, the rest of the markup byte-identical. */
export function stripLocalizationTags(html: string): string {
  if (!hasLocalizationTags(html) && !html.includes(LANG_TAG)) return html;
  // Deleting delimiters needs no pairing, which is what makes this correct on nested
  // tags and on a delimiter an edit left unbalanced.
  return html
    .replace(new RegExp(OPEN_TAG_SOURCE, "g"), "")
    .replace(new RegExp(CLOSE_TAG_SOURCE, "g"), "")
    .replace(new RegExp(LANG_OPEN_TAG_SOURCE, "g"), "")
    .replace(new RegExp(LANG_CLOSE_TAG_SOURCE, "g"), "");
}

/** Every tag keyed under `from` rekeyed under `to`, keeping its core, ordinal, slot and off flag. */
export function moveTagSurface(
  html: string,
  surfaces: { from: string; to: string },
): string {
  if (!hasLocalizationTags(html)) return html;
  return html.replace(
    new RegExp(OPEN_TAG_SOURCE, "g"),
    (openTag, elementKey: string) =>
      parseElementKey(elementKey).surface === surfaces.from
        ? openTag.replace(
            elementKey,
            withSurface(
              surfaces.to,
              elementKey.slice(surfaces.from.length + 1),
            ),
          )
        : openTag,
  );
}

/** The base body's unprefixed tags and an arm's prefixed ones are foreign to each other. */
export function stripForeignLocalizationTags(
  html: string,
  surface: string | undefined,
): string {
  const cuts = matchTagSpans(html)
    .filter((span) => parseElementKey(span.elementKey).surface !== surface)
    .flatMap((span) => [
      { start: span.start, end: span.innerStart },
      { start: span.innerStart + span.inner.length, end: span.end },
    ]);
  if (cuts.length === 0) return html;
  let out = "";
  let cursor = 0;
  for (const cut of cuts) {
    out += html.slice(cursor, cut.start);
    cursor = cut.end;
  }
  return out + html.slice(cursor);
}

/** Inline elements are absent: an anchor is not the block a line lives in. */
const SEGMENT_CONTEXT_LABEL: Record<string, string> = {
  p: "Paragraph",
  h1: "Heading",
  h2: "Heading",
  h3: "Heading",
  h4: "Heading",
  h5: "Heading",
  h6: "Heading",
  li: "List item",
  blockquote: "Quote",
  div: "Text",
  td: "Table cell",
  th: "Table heading",
};

const BLOCK_OPEN_TAG_RE = new RegExp(
  `<(${[...Object.keys(SEGMENT_CONTEXT_LABEL)].join("|")})\\b[^>]*>`,
  "gi",
);

const SLOT_CONTEXT_LABEL: Partial<Record<TranslationSlot, string>> = {
  [HREF_SLOT]: "Link",
  [SRC_SLOT]: "Image",
  [ALT_SLOT]: "Image alt text",
};

/** The push image is labelled by its `src` slot, which {@link contextLabel} checks first. */
const CORE_CONTEXT_LABEL: Record<string, string> = {
  [SUBJECT_CORE]: "Subject",
  [PREHEADER_CORE]: "Preheader",
  [TITLE_CORE]: "Title",
  [SUBTITLE_CORE]: "Subtitle",
  [BODY_CORE]: "Body",
};

/**
 * Cosmetic; never participates in identity.
 *
 * A text element is labelled by the nearest block opened before it, not by whatever
 * tag immediately precedes it: the anchor sits inside any inline wrapper the peel
 * stepped over, so the character before a tag is usually a `<span style=…>`.
 */
function contextLabel(
  html: string,
  offset: number,
  slot: TranslationSlot,
  elementKey: string,
): string {
  const bySlot = SLOT_CONTEXT_LABEL[slot];
  if (bySlot !== undefined) return bySlot;
  const byCore = CORE_CONTEXT_LABEL[keyCore(elementKey)];
  if (byCore !== undefined) return byCore;
  const before = html.slice(0, offset);
  let tag: string | null = null;
  for (const match of before.matchAll(
    new RegExp(BLOCK_OPEN_TAG_RE.source, "gi"),
  )) {
    tag = match[1]?.toLowerCase() ?? tag;
  }
  return (tag && SEGMENT_CONTEXT_LABEL[tag]) ?? "Text";
}

/** "Paragraph 1", "Paragraph 2" — one-of-a-kind labels are left bare. */
function numberRepeatedLabels(elements: LocalizableElement[]): void {
  const totals = new Map<string, number>();
  for (const element of elements) {
    totals.set(element.context, (totals.get(element.context) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  for (const element of elements) {
    const total = totals.get(element.context) ?? 0;
    if (total < 2) continue;
    const ordinal = (seen.get(element.context) ?? 0) + 1;
    seen.set(element.context, ordinal);
    element.context = `${element.context} ${ordinal}`;
  }
}

/**
 * Every translatable element of a stamped body, in document order.
 *
 * The single derivation of what can be translated — CSV rows, coverage table and
 * publish gate all read this, so what a marketer sees and what the render resolves are
 * the same set by construction.
 *
 * Deduplication should never fire, since the stamper re-mints repeated keys; it stays as
 * a total-function guarantee for a body that reaches here with the same tag twice. A key
 * outside the minted grammar is listed rather than skipped, because keys arrive that this
 * module did not mint — a content block's own, and the Beefree lane's uuid-derived ones.
 * A tag naming no valid slot is the one thing skipped.
 */
export function readStampedElements(html: string): LocalizableElement[] {
  const elements: LocalizableElement[] = [];
  const seen = new Set<string>();
  for (const span of matchTagSpans(html)) {
    const slot = span.slot;
    if (slot === null) continue;
    const sourceText = tagSourceText(slot, span.inner, span.elementKey);
    if (sourceText.length === 0 || span.elementKey.length === 0) continue;
    const element = localizableElement({
      elementKey: span.elementKey,
      slot,
      context: contextLabel(html, span.start, slot, span.elementKey),
      sourceText,
      off: span.off,
    });
    const key = translationRowId(element);
    if (seen.has(key)) continue;
    seen.add(key);
    elements.push(element);
  }
  numberRepeatedLabels(elements);
  return elements;
}
